import { Redo2, Undo2 } from "lucide-react";
import { Button } from "../ui/button";

import { useRef } from "react";
import { usePanel } from "@/context/panel-context";
import { cn } from "@/lib/utils";
import { TabsType } from "@/types/diagram";
import { panels } from "@/lib/constants";
import { useDiagram } from "@/context/diagram-context";
import { useLibrary } from "@/context/library-context";
import { useHistory } from "@/context/history-context";
import { loadDiagram } from "@/lib/diagram-utils";

const ArchitectureTabHeader = ({
  tabControls,
}: {
  tabControls: {
    activeTab: TabsType;
    setActiveTab: React.Dispatch<React.SetStateAction<TabsType>>;
  };
}) => {
  const diagram = useDiagram();
  const { handleMissingDependencies, setComponents } = useLibrary();
  const history = useHistory();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { panelType } = usePanel();

  const { activeTab, setActiveTab } = tabControls;

  const isRightPanelOpen = panelType !== null;

  const handleUndo = () => {
    const previousState = history.undo();
    if (previousState) {
      diagram.setDiagramComponents(previousState);
    }
  };

  const handleRedo = () => {
    const nextState = history.redo();
    if (nextState) {
      diagram.setDiagramComponents(nextState);
    }
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      await loadDiagram(file, {
        handleMissingDependencies,
        setComponents,
        setDiagramComponents: diagram.setDiagramComponents,
        setErrorMessage: (_: string | null) => {
        },
      });
      event.target.value = "";
    }
  };

  return (
    <div className="flex flex-col gap-2 px-2 py-2">
      {/* Bottom row: Tabs and Undo/Redo */}
      <div className="flex gap-2 items-center">
        {/* Tabs container */}
        <div
          className={cn(
            "flex gap-2 flex-1 min-w-0",
            isRightPanelOpen
              ? "overflow-x-auto scrollbar-thin scrollbar-thumb-surface-border scrollbar-track-surface-dark"
              : "overflow-x-auto"
          )}
        >
          {panels.filter((panel) => panel.id !== "chat").map((panel) => (
            <button
              key={panel.id}
              onClick={() => setActiveTab(panel.id)}
              className={cn(
                "relative rounded p-2 flex-shrink-0 transition-colors duration-200 text-xs",
                activeTab === panel.id
                  ? "bg-primary hover:bg-primary-dark"
                  : "bg-surface hover:bg-surface-hover"
              )}
            >
              {/* Hidden bold reference text */}
              <span
                aria-hidden="true"
                className="block font-bold invisible whitespace-nowrap"
              >
                {panel.label}
              </span>

              {/* Visible text layer */}
              <span
                className={cn(
                  "absolute inset-0 flex items-center justify-center transition-colors duration-200",
                  activeTab === panel.id
                    ? "font-bold text-[#FFFFFF]"
                    : "font-normal text-white"
                )}
              >
                {panel.label}
              </span>
            </button>
          ))}
        </div>

        {/* Undo/Redo buttons */}
        <div className="flex gap-1 flex-shrink-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleUndo}
            disabled={!history.canUndo}
            aria-label="Undo"
            className="p-1 hover:bg-surface-hover"
          >
            <Undo2
              className={`w-4 h-4 ${
                history.canUndo ? "text-primary" : "text-muted"
              }`}
            />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleRedo}
            disabled={!history.canRedo}
            aria-label="Redo"
            className="p-1 hover:bg-surface-hover"
          >
            <Redo2
              className={`w-4 h-4 ${
                history.canRedo ? "text-primary" : "text-muted"
              }`}
            />
          </Button>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept=".json"
        onChange={handleFileChange}
        className="hidden"
      />
    </div>
  );
};

export default ArchitectureTabHeader;
